<template>
  <custom-callout class="mt-3 custom-callout" :border-color="statusColor">
    <div class="row align-items-center">
      <div class="col-12 col-lg-2 compu-minute-result__status">
        <div class="compu-minute-result__status-header">
          <span
            class="compu-minute-result__status-dot"
            :style="{ backgroundColor: statusColor }"
          />
          <span class="compu-minute-result__status-text">
            {{ statusLabel }}
          </span>
        </div>
        <div class="compu-minute-result__number">
          {{ item.compuMinuteNumber || '-' }}
        </div>
      </div>

      <div class="col-12 col-lg-8 compu-minute-result__details p-2">
        <div class="row">
          <div class="col-12 col-md-4">
            <div class="compu-minute-result__label">Estudiante:</div>
            <div class="compu-minute-result__value">
              {{ studentDisplay }}
            </div>
          </div>

          <div class="col-12 col-md-4">
            <div class="compu-minute-result__label">Año Académico:</div>
            <div class="compu-minute-result__value">
              {{ item.academicYearName || '-' }}
            </div>
          </div>

          <div class="col-12 col-md-4">
            <div class="compu-minute-result__label">Fecha de Reunión:</div>
            <div class="compu-minute-result__value">
              {{ formatDate(item.meetingDate) }}
            </div>
          </div>

          <div class="col-12 col-md-4 mt-3">
            <div class="compu-minute-result__label">Última Modificación:</div>
            <div class="compu-minute-result__value">
              {{ formatDate(item.lastModifiedDate) }}
            </div>
          </div>

          <div class="col-12 mt-3">
            <div class="compu-minute-result__label">Propósitos:</div>
            <div class="compu-minute-result__chips">
              <span v-if="primaryPurpose" class="compu-minute-result__chip">
                {{ primaryPurpose }}
              </span>
              <span
                v-if="additionalPurposeCount"
                class="compu-minute-result__chip compu-minute-result__chip--count"
              >
                +{{ additionalPurposeCount }}
              </span>
              <span v-if="!primaryPurpose" class="compu-minute-result__value">
                -
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-2 mt-3 mt-lg-0">
        <dropdown
          class="w-100"
          name="Actions"
          title="Acciones"
          :items="actions"
        />
      </div>
    </div>
  </custom-callout>
</template>

<script>
import CustomCallout from 'custom-callout';
import Dropdown from 'dropdown';
import moment from 'moment';
import { getCompuMinuteStatusColor } from '../helpers/compu-minute-status.helper';
import compuMinuteStatus from '@/utils/constants/compuMinuteStatus.js';

export default {
  name: 'CompuMinuteResultRow',
  components: {
    CustomCallout,
    Dropdown,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    canReturnToDraft: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    statusColor() {
      return getCompuMinuteStatusColor(
        this.item.statusId,
        this.item.statusName
      );
    },
    statusLabel() {
      return this.item.statusName || '-';
    },
    purposes() {
      return this.item.purposes || [];
    },
    studentDisplay() {
      const studentName = this.item.studentName || '-';
      const studentSieId = this.item.studentSieId || '-';

      return `${studentName} - ${studentSieId}`;
    },
    primaryPurpose() {
      const purpose = this.purposes[0];

      if (!purpose) return null;

      return this.getPurposeLabel(purpose);
    },
    additionalPurposeCount() {
      return Math.max(this.purposes.length - 1, 0);
    },
    actions() {
      const statusId = Number(this.item.statusId);

      if (statusId === compuMinuteStatus.Borrador) {
        return [
          this.getEditAction(),
          {
            label: 'Descartar Borrador',
            icon: 'fas fa-times-circle text-danger',
            click: () => this.$emit('discard-draft', this.item),
          },
        ];
      }

      if (
        statusId === compuMinuteStatus.Completada ||
        statusId === compuMinuteStatus.EnProcesoFirma
      ) {
        const actions = [
          this.getViewAction(),
          this.getStatusHistoryAction(),
          this.getPrintAction(),
        ];

        if (this.canShowReturnToDraftAction) {
          actions.push(this.getReturnToDraftAction());
        }

        return actions;
      }

      if (
        statusId === compuMinuteStatus.FirmaRechazada ||
        statusId === compuMinuteStatus.Cancelada ||
        statusId === compuMinuteStatus.DocumentoCancelado
      ) {
        const actions = [this.getViewAction(), this.getStatusHistoryAction()];

        if (this.canShowReturnToDraftAction) {
          actions.push(this.getReturnToDraftAction());
        }

        return actions;
      }

      const actions = [this.getViewAction()];

      if (this.canShowReturnToDraftAction) {
        actions.push(this.getReturnToDraftAction());
      }

      return actions;
    },
    canShowReturnToDraftAction() {
      const statusId = Number(this.item.statusId);
      const canReturnByStatus =
        statusId === compuMinuteStatus.FirmaRechazada ||
        statusId === compuMinuteStatus.Cancelada ||
        statusId === compuMinuteStatus.DocumentoCancelado;
      const canShow =
        statusId !== compuMinuteStatus.Borrador &&
        (canReturnByStatus || this.canReturnToDraft);

      return canShow;
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return '-';

      return moment(date)
        .locale('es')
        .format('DD/MMM/YYYY');
    },
    getPurposeLabel(purpose) {
      if (purpose.otherDescription) {
        return `Otro: ${purpose.otherDescription}`;
      }

      return purpose.name || '-';
    },
    getEditAction() {
      return {
        label: 'Editar',
        icon: 'fas fa-edit color-primary-1',
        click: () => this.$emit('edit', this.item),
      };
    },
    getViewAction() {
      return {
        label: 'Ver',
        icon: 'fas fa-eye color-primary-1',
        click: () => this.$emit('edit', this.item),
      };
    },
    getStatusHistoryAction() {
      return {
        label: 'Historial de Estatus',
        icon: 'fas fa-history color-primary-1',
        click: this.handlePendingAction,
      };
    },
    getPrintAction() {
      return {
        label: 'Imprimir',
        icon: 'fas fa-print color-primary-1',
        click: () => this.$emit('print', this.item),
      };
    },
    getReturnToDraftAction() {
      return {
        label: 'Devolver a Borrador',
        icon: 'fas fa-undo color-primary-1',
        click: () => this.$emit('return-to-draft', this.item),
      };
    },
    handlePendingAction() {},
  },
};
</script>

<style scoped>
.compu-minute-result__status {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 72px;
  gap: 8px;
}

.compu-minute-result__status-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.compu-minute-result__status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 10px;
}

.compu-minute-result__status-text {
  color: #11291b;
  font-size: 16px;
  font-weight: 700;
}

.compu-minute-result__number {
  color: #11291b;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
}
.compu-minute-result__value {
  color: #11291b;
  font-size: 16px;
}

.compu-minute-result__details {
  border-left: 1px solid #e2e2e2;
}

.compu-minute-result__label {
  color: #727272;
  font-size: 14px;
  font-weight: 400;
}

.compu-minute-result__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.compu-minute-result__chip {
  border: 1px solid #c8d0cc;
  border-radius: 18px;
  color: #11291b;
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 4px 14px;
  font-size: 14px;
  font-weight: 600;
  background-color: #ffffff;
}

.compu-minute-result__chip--count {
  min-width: 36px;
  justify-content: center;
}

@media (max-width: 991px) {
  .compu-minute-result__details {
    border-left: 0;
  }
}
</style>
